import React, { useContext, forwardRef } from 'react';
import PropTypes from 'prop-types';
import { propType } from 'graphql-anywhere';
import cx from 'classnames';
import gql from 'graphql-tag';

import { SEGMENT_PAGE_CONTEXTS } from '../constants';
import { NAME_PLURAL_BY_INTEGRATION_ITEM_KIND } from '../constants-shared';

import { dataIDFromObject } from '../apollo';

import withData from '../decorators/withData';

import getSegmentMainDisplayObjectVariables from '../utils/getSegmentMainDisplayObjectVariables';

import { track } from '../services/Analytics';

import AccountLink from './links/AccountLink';
import { DataContext } from './Data';
import IntegrationsStatusBanner from './IntegrationsStatusBanner';
import MainDisplayObjectLink from './links/MainDisplayObjectLink';
import SearchHighlightedText from './SearchHighlightedText';

// for performance reasons, this includes only the fields needed for links
export const autocompleteMainDisplayObjectFragment = gql`
  fragment autocompleteMainDisplayObjectFragment on MainDisplayObject {
    ... on Conversation {
      dokkioClass
      accountID
      id
      name
      conversationOrigin
    }
    ...on IntegrationItem {
      dokkioClass
      accountID
      integrationID
      integrationItemKind
      integrationItemType
      remoteID
      name
      shortcutRemoteID
      shortcutIntegrationID
    }
    ...on DokkioUser {
      dokkioClass
      accountID
      id
      primaryDisplayName
    }
    ...on DokkioTag {
      dokkioClass
      accountID
      id
      dokkioLabelID
      name
    }
    ...on IntegrationEmail {
      dokkioClass
      accountID
      integrationID
      remoteID
      subject
    }
    ...on IntegrationThread {
      dokkioClass
      accountID
      integrationID
      remoteID
      integrationEmails {
        dokkioClass
        integrationID
        remoteID
        subject
      }
    }
    ... on Webpage {
      dokkioClass
      accountID
      id
      name
      url
    }
  }
`;

const LABEL_BY_GROUP_TYPE = {
  conversation: 'Chats',
  dokkio_tag: 'Tags',
  integration_email: 'Emails',
  integration_thread: 'Emails',
  webpage: 'Pages',
  dokkio_user: 'Users',
};

export const SearchAutocompleteResults = forwardRef(({
  dataForDisplay,
  loading,
  onLinkMouseEnter,
  onLinkNavigation,
  q,
  segmentPageCtxt,
  selectedIndex,
  setLinkRef,
}, ref) => {
  const {
    dokkioLabelByID,
    integrationByID,
  } = useContext(DataContext);
  const getGroupHeadingLinkTo = ({ groupType }) => {
    const filters = { q };
    if (NAME_PLURAL_BY_INTEGRATION_ITEM_KIND[groupType]) {
      filters.integrationItemKinds = [groupType];
    } else {
      filters.searchResultTypes = [groupType];
    }
    return `search?search=${JSON.stringify(filters)}`;
  };

  const getGroupHeadingLinkLabel = ({ groupType }) => {
    if (NAME_PLURAL_BY_INTEGRATION_ITEM_KIND[groupType]) {
      return NAME_PLURAL_BY_INTEGRATION_ITEM_KIND[groupType];
    }
    return LABEL_BY_GROUP_TYPE[groupType];
  };

  const onClickFullTextSearchResultsLink = (location) => {
    track('Search Keyword', {
      SearchClick: `Auto${location}Hdr`,
      SearchText: q,
      PageCtxt: segmentPageCtxt,
    });
    onLinkNavigation(false);
  };

  const onClickGroupHeadingLink = ({ groupType }) => {
    track('Search AutoToGroup', {
      GroupType: getGroupHeadingLinkLabel({ groupType }),
      SearchText: q,
      PageCtxt: segmentPageCtxt,
    });
    onLinkNavigation();
  };

  const onClickObjectLink = ({ groupType, mainDisplayObject }) => {
    track('Search AutoToObject', {
      GroupType: getGroupHeadingLinkLabel({ groupType }),
      SearchText: q,
      ...getSegmentMainDisplayObjectVariables({
        dokkioLabelByID,
        integrationByID,
        mainDisplayObject,
      }),
      PageCtxt: segmentPageCtxt,
    });
    onLinkNavigation();
  };

  if (!q
    || q.length < 2
    || (!loading && !dataForDisplay.length)) {
    return null;
  }

  let resultsContent;
  if (!loading) {
    let combinedLength = 0;

    resultsContent = (
      // See MOBILE_HEIGHT_OFFSET_PX in `Search`
      <div className="max-h-[calc(100vh_-_190px)] desktop:max-h-full overflow-y-auto divide-y divide-gray-200 dark:divide-gray-800 text-sm">
        {dataForDisplay.map(({
          groupType,
          total,
          mainDisplayObjects,
        }) => {
          const groupContent = (
            <div
              className="flex gap-x-2 p-2"
              key={groupType}
            >
              <div className="flex flex-none items-center justify-end h-[25px] w-[125px] desktop:px-2 space-x-2">
                <AccountLink
                  onClick={() => onClickGroupHeadingLink({ groupType })}
                  to={getGroupHeadingLinkTo({ groupType })}
                >
                  {getGroupHeadingLinkLabel({ groupType })}
                </AccountLink>
                <span className="text-gray-500">({total})</span>
              </div>
              <div className="flex flex-col flex-auto min-w-0">
                {mainDisplayObjects.map((mainDisplayObject, itemIndex) => {
                  const index = itemIndex + combinedLength;

                  return (
                    <MainDisplayObjectLink
                      innerRef={aRef => setLinkRef({ index, ref: aRef })}
                      mainDisplayObject={mainDisplayObject}
                      className={cx('dokkio-ellipse h-[25px] px-2 py-0.5 text-gray-700 dark:text-gray-300', {
                        'bg-yellow-200 dark:bg-yellow-800': index === selectedIndex,
                      })}
                      onClick={() => onClickObjectLink({ groupType, mainDisplayObject })}
                      onKeyDown={() => onClickObjectLink({ groupType, mainDisplayObject })}
                      key={dataIDFromObject(mainDisplayObject)}
                      segmentClickCtxt="ItemName"
                      segmentPageCtxt={segmentPageCtxt}
                      onMouseEnter={onLinkMouseEnter(index)}
                      disablePopover
                      renderName={mainDisplayObjectName => (
                        <SearchHighlightedText
                          q={q}
                          text={mainDisplayObjectName}
                          isPrefixMatching
                        />
                      )}
                    />
                  );
                })}
              </div>
            </div>
          );

          combinedLength += mainDisplayObjects.length;
          return groupContent;
        })}
      </div>
    );
  }

  let headingContent;
  if (loading) {
    headingContent = 'Loading...';
  } else {
    headingContent = (
      <div className="flex flex-col desktop:flex-row items-center justify-between">
        <div><span className="font-bold">Name-only</span> matches</div>
        <AccountLink
          to={`/search?search=${JSON.stringify({ q })}`}
          onClick={() => onClickFullTextSearchResultsLink('Top')}
        >
          Full-text search results &raquo;
        </AccountLink>
      </div>
    );
  }

  return (
    <div ref={ref} className="max-w-[90vw] desktop:min-w-[500px] desktop:max-w-[calc(100vw_-_250px)] border border-gray-200 dark:border-gray-800 text-base">
      <div className="border-b border-gray-200 dark:border-gray-800 bg-gray-50 dark:bg-gray-950 rounded-t-lg px-4 py-2">
        {headingContent}
      </div>
      <IntegrationsStatusBanner
        className="desktop:pl-4"
        segmentPageCtxt={segmentPageCtxt}
      />
      {resultsContent}
      {!loading && (
        <div className="border-t border-gray-200 dark:border-gray-800 bg-gray-50 dark:bg-gray-950 rounded-b-lg px-4 py-2 text-center">
          <AccountLink
            to={`/search?search=${JSON.stringify({ q })}`}
            onClick={() => onClickFullTextSearchResultsLink('Bot')}
          >
            Full-text search results &raquo;
          </AccountLink>
        </div>
      )}
    </div>
  );
});

SearchAutocompleteResults.propTypes = {
  dataForDisplay: PropTypes.arrayOf(PropTypes.shape({
    dokkioClass: PropTypes.string.isRequired,
    groupType: PropTypes.string.isRequired,
    total: PropTypes.number.isRequired,
    mainDisplayObjects: PropTypes
      .arrayOf(propType(autocompleteMainDisplayObjectFragment))
      .isRequired,
  })).isRequired,
  loading: PropTypes.bool.isRequired,
  onLinkNavigation: PropTypes.func,
  onLinkMouseEnter: PropTypes.func,
  q: PropTypes.string.isRequired,
  segmentPageCtxt: PropTypes.oneOf(SEGMENT_PAGE_CONTEXTS).isRequired,
  selectedIndex: PropTypes.number,
  setLinkRef: PropTypes.func,
};

SearchAutocompleteResults.defaultProps = {
  onLinkNavigation: () => null,
  onLinkMouseEnter: () => null,
  selectedIndex: null,
  setLinkRef: () => null,
};

export default withData()(SearchAutocompleteResults);
